import { ErrorHandler, Injectable, inject } from '@angular/core';
import { SentryErrorHandler } from '@advisor-ui/app-services';
import { PostHogService } from './posthog.service';

/** Most PostHog exceptions sent per page load for one error type and message. */
export const MAX_EXCEPTIONS_PER_MESSAGE = 5;
/** Most PostHog exceptions sent per page load in total, for loops whose message varies. */
export const MAX_EXCEPTIONS_PER_PAGE = 50;

@Injectable()
export class PostHogErrorHandler implements ErrorHandler {
    private readonly posthogService = inject(PostHogService);
    private readonly sentryErrorHandler = inject(SentryErrorHandler);

    private readonly sentByMessage = new Map<string, number>();
    private sentTotal = 0;

    handleError(error: unknown): void {
        const originalError = this.extractError(error);
        if (this.withinRateLimit(originalError)) {
            this.posthogService.posthog.captureException(originalError);
        }
        this.sentryErrorHandler.handleError(error);
    }

    // posthog.captureException() skips posthog-js's own exception rate limiter, which only
    // guards autocaptured exceptions. Without a cap here, an error thrown on every change
    // detection pass (NG0103) sends thousands of identical events from one page.
    private withinRateLimit(error: unknown): boolean {
        if (this.sentTotal >= MAX_EXCEPTIONS_PER_PAGE) {
            return false;
        }

        const key = this.rateLimitKey(error);
        const sent = this.sentByMessage.get(key) ?? 0;
        if (sent >= MAX_EXCEPTIONS_PER_MESSAGE) {
            return false;
        }

        this.sentByMessage.set(key, sent + 1);
        this.sentTotal++;
        return true;
    }

    private rateLimitKey(error: unknown): string {
        if (error && typeof error === 'object') {
            const { name, message } = error as { name?: unknown; message?: unknown };
            return `${String(name)}: ${String(message)}`;
        }

        return String(error);
    }

    private extractError(error: unknown): unknown {
        if (error && typeof error === 'object' && 'ngOriginalError' in error) {
            return error.ngOriginalError;
        }

        return error;
    }
}
